import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedService } from '@services/shared.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { CompanionCardComponent } from '../companion/companion-card.component';
import {
  ClientConversationLogService,
  ClientLogAccount,
  ClientLogApiError,
  ClientLogConversation,
  ClientLogDashboard,
  ClientLogItem,
  ClientLogNumberStat,
  ClientLogsQuery,
} from '@services/companion.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

export type EmojiCategory = 'smileys' | 'hands' | 'hearts' | 'objects';

/**
 * WhatsApp Chat Log.
 *
 * Everything on this screen comes from `whatsapp_client_conversation_log`,
 * which is scoped server-side to the numbers connected to the logged-in
 * account — there is no account id to pass and nothing to filter client-side
 * for security. The endpoint always answers HTTP 200, so a refusal surfaces as
 * a {@link ClientLogApiError} and is shown in `loadError` rather than silently
 * leaving the screen blank.
 *
 * Sidebar rows come from the `conversations` resource and the open thread from
 * `conversation` (oldest first). When a filter that `conversation` does not
 * accept is active (direction / date / free-text search), the thread is read
 * from the filtered `logs` feed instead and flipped into chronological order.
 */
@Component({
  selector: 'app-whatsapp-conversation-log',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzToolTipModule,
    NzDrawerModule,
    NzModalModule,
    NzSelectModule,
    NzDatePickerModule,
    NzTagModule,
    NzInputModule,
    NzTabsModule,
    TranslateModule,
    CompanionCardComponent,
  ],
  templateUrl: './whatsapp-conversation-log.component.html',
  styleUrls: ['./whatsapp-conversation-log.component.scss'],
})
export class WhatsappConversationLogComponent implements OnInit, OnDestroy {
  private clientLog = inject(ClientConversationLogService);
  private sharedService = inject(SharedService);
  private secureStorage = inject(SecureStorageService);
  private translateService = inject(TranslateService);
  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);

  /**
   * Gates the Campaign Companion connection card, which moved here from
   * Settings: it belongs next to the log it produces. Matching the card's own
   * guard, this is false only when the flag was explicitly stored as false —
   * an account that has never had it resolved still sees the card.
   */
  hasCompanionAccess = this.secureStorage.getItem<boolean>('hasCompanionAccess') !== false;

  // ── Account context ────────────────────────────────────────────────────────
  account: ClientLogAccount | null = null;
  dashboard: ClientLogDashboard | null = null;

  // ── Chat mode ──────────────────────────────────────────────────────────────
  conversations: ClientLogConversation[] = [];
  activeChatPhone: string | null = null;
  threadMessages: ClientLogItem[] = [];
  threadTotal = 0;
  threadPage = 1;
  threadPageSize = 50;
  threadLoading = false;
  /**
   * A further page being fetched by scrolling, as opposed to the first page.
   *
   * Kept apart from `threadLoading` because the two mean different things to
   * the view: the first page replaces the thread and may show an empty state,
   * a scrolled page only adds a spinner above while the messages already on
   * screen stay put.
   */
  threadLoadingMore = false;

  /** The pane: read to detect the top, written to pin the view to the bottom. */
  @ViewChild('threadScroll') threadScrollRef?: ElementRef<HTMLElement>;

  /** How close to the top counts as "asking for older messages", in pixels. */
  private static readonly THREAD_SCROLL_THRESHOLD_PX = 120;

  // ── Numbers report mode ────────────────────────────────────────────────────
  numberStats: ClientLogNumberStat[] = [];

  // Rows behind the data table (the `logs` feed).
  items: ClientLogItem[] = [];
  totalItems = 0;
  loading = false;
  currentPage = 1;
  pageSize = 25;

  /** Set when the endpoint refuses (`ok: false`) or the request fails. */
  loadError: string | null = null;

  // Active mode: 'chat' | 'tokens' ('tokens' is the per-number report)
  reportType: 'chat' | 'tokens' = 'chat';

  // View Mode: WhatsApp chat UI vs data table
  viewMode: 'chat' | 'table' = 'chat';

  // Filter payload state
  searchTerm = '';
  phoneFilter = '';
  directionFilter = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  threadSearchTerm = '';

  // Filter modal state
  filterDrawerVisible = false;

  // Detail inspection drawer
  drawerVisible = false;
  selectedItem: any | null = null;
  rawEntries: { key: string; value: string }[] = [];

  // Summary metrics shown in the KPI cards
  summaryMetrics = {
    totalLogs: 0,
    inboundCount: 0,
    outboundCount: 0,
    sessions: 0,
    contests: 0,
    activePhones: 0,
  };

  // Emoji & attachment popup state (message composer)
  newMessageText = '';
  emojiPickerVisible = false;
  attachmentMenuVisible = false;
  emojiCategory: EmojiCategory = 'smileys';
  emojiSearchTerm = '';

  emojiCategories: { id: EmojiCategory; icon: string; label: string }[] = [
    { id: 'smileys', icon: '😀', label: 'Smileys' },
    { id: 'hands', icon: '👍', label: 'Hands' },
    { id: 'hearts', icon: '❤️', label: 'Hearts' },
    { id: 'objects', icon: '🎯', label: 'Objects' },
  ];

  emojiListMap: Record<string, string[]> = {
    smileys: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋',
      '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐',
      '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮', '😯',
      '😴', '😌', '😎', '🤓', '🧐', '🥳', '🤠', '😲', '😳', '🥺',
      '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣',
      '😞', '😓', '😩', '😫', '😮‍💨', '😤', '😡', '😠', '🤬', '😈',
      '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾'
    ],
    hands: [
      '👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙',
      '👈', '👉', '👆', '🖕', '👇', '☝️', '🖐️', '✋', '🖖', '👋',
      '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪'
    ],
    hearts: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '✨',
      '🌟', '⭐', '💫', '💥', '🔥', '💧', '💨', '☀️', '🌙', '⚡'
    ],
    objects: [
      '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️',
      '🎫', '🎟️', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹',
      '📱', '📲', '💻', '⌨️', '🖥️', '📸', '📹', '🔔', '💡', '💰',
      '💳', '💎', '🚀', '🎯', '✅', '💯', '📩', '📦', '🔮', '🔑'
    ]
  };

  readPhoneThreads = new Set<string>();

  /** Rendered message bodies, keyed by message id — see formatMessageBody. */
  private formattedBodies = new Map<string, SafeHtml>();

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  /**
   * Requests currently holding the page loader up.
   *
   * The shared loader is a single on/off flag, while opening this screen takes
   * several requests (totals, sidebar, then the open thread). Counting them
   * keeps the loader up until the last one answers instead of dropping it
   * after the first and showing empty cards in between.
   */
  private pendingLoads = 0;

  private beginLoad(): void {
    if (this.pendingLoads++ === 0) this.sharedService.showLoader();
  }

  private endLoad(): void {
    if (this.pendingLoads === 0) return;
    if (--this.pendingLoads === 0) this.sharedService.hideLoader();
  }

  ngOnInit(): void {
    this.loadReadThreads();
    this.loadEmojisFromApi();

    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 1;
        if (this.reportType === 'chat') {
          // No full-screen loader while the user is still typing.
          this.reloadActiveThread(undefined, false);
        } else {
          this.fetchData(1, false);
        }
      });

    this.loadAccount();
    this.loadDashboard();
    this.fetchData(1);
  }

  ngOnDestroy(): void {
    // Requests cut short by destroy$ never answer, so the loader is released here.
    if (this.pendingLoads > 0) {
      this.pendingLoads = 0;
      this.sharedService.hideLoader();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  /** `me` gives the company id in the chat header and the readable numbers. */
  private loadAccount(): void {
    this.clientLog
      .getAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (account) => (this.account = account),
        error: (err) => console.warn('WhatsApp account info unavailable:', err),
      });
  }

  /** KPI cards read the server-side totals, not the current page. */
  private loadDashboard(): void {
    this.beginLoad();
    this.clientLog
      .getDashboard(30)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.endLoad();
          this.dashboard = data;
          this.summaryMetrics = {
            totalLogs: data.totals?.messages || 0,
            inboundCount: data.totals?.inbound || 0,
            outboundCount: data.totals?.outbound || 0,
            sessions: data.totals?.sessions || 0,
            contests: data.totals?.contests || 0,
            activePhones: data.numbers || 0,
          };
        },
        error: (err) => {
          this.endLoad();
          console.warn('WhatsApp dashboard totals unavailable:', err);
        },
      });
  }

  fetchData(page: number = 1, showLoader: boolean = true): void {
    if (showLoader) this.beginLoad();
    this.loading = true;
    this.loadError = null;

    if (this.reportType === 'tokens') {
      this.loadNumberStats(showLoader);
      return;
    }

    this.currentPage = page;
    this.clientLog
      .getConversations()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.conversations = rows || [];
          this.loading = false;

          const visible = this.getGroupedPhoneThreads();
          const stillThere = visible.some((t) => t.phone === this.activeChatPhone);
          if (!stillThere) {
            this.activeChatPhone = visible.length ? visible[0].phone : null;
          }
          // The thread takes the loader over before this request lets go of
          // it, so it stays up until the messages are on screen.
          this.reloadActiveThread(undefined, showLoader);
          if (showLoader) this.endLoad();
        },
        error: (err) => this.handleLoadError(err, showLoader),
      });
  }

  private loadNumberStats(showLoader: boolean): void {
    this.clientLog
      .getNumberStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.numberStats = rows || [];
          this.items = [];
          this.totalItems = this.numberStats.length;
          this.loading = false;
          if (showLoader) this.endLoad();
        },
        error: (err) => this.handleLoadError(err, showLoader),
      });
  }

  /**
   * Opens the thread at its newest messages, the way a chat app does.
   *
   * `page` is only passed by the table view's paginator. Left out, the thread
   * opens at the end -- which page that is depends on the feed, see
   * `newestPageFor`.
   */
  reloadActiveThread(page?: number, showLoader = true, pinToNewest = page === undefined): void {
    if (!this.activeChatPhone) {
      this.threadMessages = [];
      this.threadTotal = 0;
      this.threadPage = 1;
      return;
    }

    const openAtNewest = page === undefined;
    const needsLogsFeed = this.usesLogsFeed();
    const requestedPage = openAtNewest ? this.newestPageFor(needsLogsFeed) : page;

    this.threadPage = requestedPage;
    this.threadLoading = true;
    const phone = this.activeChatPhone;
    if (showLoader) this.beginLoad();

    const request = needsLogsFeed
      ? this.clientLog.getLogs(this.buildLogsQuery(phone, requestedPage))
      : this.clientLog.getConversation(phone, requestedPage, this.threadPageSize);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const rows = result.items || [];
        this.threadMessages = needsLogsFeed ? [...rows].reverse() : rows;
        this.threadTotal = result.total || rows.length;
        this.items = rows;
        this.totalItems = this.threadTotal;
        this.threadLoading = false;
        this.loadError = null;

        // `newestPageFor` guessed the last page from the sidebar's message
        // count, which can lag behind the thread. The response knows the real
        // page count, so a wrong guess is corrected here rather than leaving
        // the client somewhere in the middle of the conversation.
        const lastPage = this.lastPageFrom(result);
        if (openAtNewest && !needsLogsFeed && lastPage > 0 && lastPage !== requestedPage) {
          // Still opening at the newest, so the corrected page must pin to
          // the bottom too; it takes the loader over before this one lets go.
          this.reloadActiveThread(lastPage, showLoader, true);
          if (showLoader) this.endLoad();
          return;
        }

        if (showLoader) this.endLoad();
        if (pinToNewest) this.scrollThreadToBottom();
      },
      error: (err) => {
        if (showLoader) this.endLoad();
        this.threadMessages = [];
        this.threadTotal = 0;
        this.threadLoading = false;
        this.loadError = this.describeError(err);
      },
    });
  }

  /**
   * Whether the open thread is being served by the `logs` feed.
   *
   * `conversation` only takes a phone, so the moment a search term, direction
   * or date narrows the thread it has to come from `logs` instead. That
   * matters beyond the request itself: the two feeds are paged in opposite
   * directions (see `olderPageAfter`).
   */
  private usesLogsFeed(): boolean {
    return !!(
      this.searchTerm.trim() ||
      this.directionFilter ||
      this.dateFrom ||
      this.dateTo
    );
  }

  /**
   * The page holding the newest messages.
   *
   * `logs` comes back newest-first, so its first page already is the end of
   * the conversation. `conversation` comes back oldest-first, so the end is
   * its LAST page, worked out from the message count the sidebar already
   * carries -- that keeps opening a thread to a single request instead of
   * fetching page 1 only to learn how many pages there are. The count can be
   * stale, which is why the response is checked against it afterwards.
   */
  private newestPageFor(needsLogsFeed: boolean): number {
    if (needsLogsFeed) return 1;

    const known = (this.conversations || [])
      .find((c) => c.phone_number === this.activeChatPhone)?.message_count;
    const count = Number(known) || 0;
    if (count <= 0) return 1;

    return Math.max(1, Math.ceil(count / this.threadPageSize));
  }

  /** The last page number a response implies, or 0 when it does not say. */
  private lastPageFrom(result: { total?: number; totalPages?: number }): number {
    if (Number(result?.totalPages) > 0) return Number(result.totalPages);
    const total = Number(result?.total) || 0;
    return total > 0 ? Math.max(1, Math.ceil(total / this.threadPageSize)) : 0;
  }

  /**
   * Where the page holding OLDER messages sits relative to the one on screen.
   *
   * The feeds run opposite ways: `conversation` is oldest-first, so older is
   * the page BEFORE; `logs` is newest-first, so older is the page AFTER. Both
   * are reached by scrolling up, which is all the view needs to know.
   */
  private olderPageAfter(page: number, needsLogsFeed: boolean): number {
    return needsLogsFeed ? page + 1 : page - 1;
  }

  /** Whether any messages older than the ones on screen are still unfetched. */
  get hasOlderThreadMessages(): boolean {
    if (!this.activeChatPhone) return false;
    return this.usesLogsFeed()
      ? this.threadMessages.length < this.threadTotal
      : this.threadPage > 1;
  }

  /** Pins the view to the newest message, once the rows have rendered. */
  private scrollThreadToBottom(): void {
    setTimeout(() => {
      const pane = this.threadScrollRef?.nativeElement;
      if (!pane) return;
      // Make sure the latest rows are in the DOM before measuring them.
      this.cdr.detectChanges();
      pane.scrollTop = pane.scrollHeight;
      // Emoji and web fonts can still grow the rows after the first layout,
      // which would leave the view short of the last message; pin once more.
      requestAnimationFrame(() => (pane.scrollTop = pane.scrollHeight));
      setTimeout(() => (pane.scrollTop = pane.scrollHeight), 150);
      this.fillThreadViewport();
    });
  }

  /**
   * Keeps fetching older pages while the thread is too short to scroll.
   *
   * The newest page of `conversation` is whatever is left over after the full
   * pages -- often only a message or two. Those never overflow the pane, so no
   * scroll event can fire and the older history would stay out of reach.
   */
  private fillThreadViewport(): void {
    const pane = this.threadScrollRef?.nativeElement;
    if (!pane || !this.hasOlderThreadMessages) return;
    const threshold = WhatsappConversationLogComponent.THREAD_SCROLL_THRESHOLD_PX;
    if (pane.scrollHeight - pane.clientHeight <= threshold) {
      this.loadOlderThreadMessages();
    }
  }

  /** Scrolling to the top asks for the messages before the ones on screen. */
  onThreadScroll(): void {
    const pane = this.threadScrollRef?.nativeElement;
    if (!pane) return;
    if (!this.hasOlderThreadMessages || this.threadLoading || this.threadLoadingMore) return;

    if (pane.scrollTop <= WhatsappConversationLogComponent.THREAD_SCROLL_THRESHOLD_PX) {
      this.loadOlderThreadMessages();
    }
  }

  /**
   * Loads the messages before the ones on screen and puts them above.
   *
   * Nothing already rendered is replaced: a page arriving while the user reads
   * must not move the conversation under them, which is also why the scroll
   * position is corrected once the new rows are in.
   */
  loadOlderThreadMessages(): void {
    if (!this.activeChatPhone) return;
    if (this.threadLoading || this.threadLoadingMore) return;
    if (!this.hasOlderThreadMessages) return;

    const phone = this.activeChatPhone;
    const needsLogsFeed = this.usesLogsFeed();
    const olderPage = this.olderPageAfter(this.threadPage, needsLogsFeed);
    if (olderPage < 1) return;

    this.threadLoadingMore = true;

    const request = needsLogsFeed
      ? this.clientLog.getLogs(this.buildLogsQuery(phone, olderPage))
      : this.clientLog.getConversation(phone, olderPage, this.threadPageSize);

    request.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        // The thread may have been switched or re-filtered while this was in
        // flight; merging then would splice one conversation into another.
        if (this.activeChatPhone !== phone || this.usesLogsFeed() !== needsLogsFeed) {
          this.threadLoadingMore = false;
          return;
        }

        const rows = result.items || [];
        const ordered = needsLogsFeed ? [...rows].reverse() : rows;

        if (ordered.length) {
          // Growing the list upwards pushes everything down by the new
          // content's height. Without correcting for it the message the user
          // was reading jumps off screen -- and lands them back at the top,
          // which immediately asks for another page. Measuring from the
          // bottom keeps that message in place however much is added above.
          const pane = this.threadScrollRef?.nativeElement;
          const fromBottom = pane ? pane.scrollHeight - pane.scrollTop : 0;
          // A pane that could not scroll yet was filled automatically, not by
          // the user scrolling up -- it should stay on the newest message.
          const wasShort = pane ? pane.scrollHeight <= pane.clientHeight + 1 : false;

          this.threadMessages = [...ordered, ...this.threadMessages];
          this.threadPage = olderPage;
          if (result.total) this.threadTotal = result.total;
          this.items = this.threadMessages;
          this.totalItems = this.threadTotal;
          this.threadLoadingMore = false;

          if (pane) {
            if (wasShort) {
              this.scrollThreadToBottom();
            } else {
              // Rendered right now rather than in a timeout: with event
              // coalescing the change detection pass can land after a
              // setTimeout, which would measure the old height and leave the
              // view stuck at the top.
              this.cdr.detectChanges();
              pane.scrollTop = pane.scrollHeight - fromBottom;
              this.fillThreadViewport();
            }
          }
          return;
        } else if (needsLogsFeed) {
          // An empty page means the count was stale; stop asking for more.
          this.threadTotal = this.threadMessages.length;
        } else {
          // Nothing before this page after all -- treat it as the start.
          this.threadPage = 1;
        }

        if (result.total) this.threadTotal = result.total;
        this.items = this.threadMessages;
        this.totalItems = this.threadTotal;
        this.threadLoadingMore = false;
      },
      error: () => {
        // The messages already on screen stay; only the extra page is lost, so
        // this must not clear the thread the way a first-page failure does.
        this.threadLoadingMore = false;
      },
    });
  }

  private buildLogsQuery(phone: string, page: number): ClientLogsQuery {
    return {
      page,
      page_size: this.threadPageSize,
      phone,
      direction: this.directionFilter || '',
      search: this.searchTerm.trim(),
      date_from: this.dateFrom ? this.toIso(this.dateFrom, false) : '',
      date_to: this.dateTo ? this.toIso(this.dateTo, true) : '',
    };
  }

  private handleLoadError(err: any, showLoader: boolean): void {
    this.conversations = [];
    this.numberStats = [];
    this.items = [];
    this.threadMessages = [];
    this.totalItems = 0;
    this.threadTotal = 0;
    this.loading = false;
    this.loadError = this.describeError(err);
    if (showLoader) this.endLoad();
  }

  private describeError(err: any): string {
    if (err instanceof ClientLogApiError) return err.reason;
    return this.translateOrFallback(
      'whatsapp-conversation.load-failed',
      'Failed to load WhatsApp conversation logs'
    );
  }

  retryLoad(): void {
    this.loadDashboard();
    this.fetchData(this.currentPage);
  }

  // ── Mode & filters ─────────────────────────────────────────────────────────

  onReportTypeChange(type: 'chat' | 'tokens'): void {
    if (this.reportType === type) return;
    this.reportType = type;
    this.viewMode = type === 'tokens' ? 'table' : 'chat';
    this.currentPage = 1;
    this.fetchData(1);
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.fetchData(1);
  }

  onPageIndexChange(index: number): void {
    this.currentPage = index;
    this.reloadActiveThread(index);
  }

  onPageSizeChange(size: number): void {
    this.threadPageSize = size;
    this.pageSize = size;
    this.currentPage = 1;
    this.reloadActiveThread();
  }

  openFilterDrawer(): void {
    this.filterDrawerVisible = true;
  }

  closeFilterDrawer(): void {
    this.filterDrawerVisible = false;
  }

  openFilterModal(): void {
    this.filterDrawerVisible = true;
  }

  closeFilterModal(): void {
    this.filterDrawerVisible = false;
  }

  applyFilters(): void {
    this.currentPage = 1;
    if (this.phoneFilter) this.activeChatPhone = this.phoneFilter;
    this.reloadActiveThread();
    this.closeFilterDrawer();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.phoneFilter = '';
    this.directionFilter = '';
    this.dateFrom = null;
    this.dateTo = null;
    this.currentPage = 1;
    this.fetchData(1);
  }

  hasActiveFilters(): boolean {
    return !!(
      this.searchTerm ||
      this.phoneFilter ||
      this.directionFilter ||
      this.dateFrom ||
      this.dateTo
    );
  }

  // ── Sidebar threads ────────────────────────────────────────────────────────

  getGroupedPhoneThreads(): {
    phone: string;
    displayName: string;
    lastMessage: string;
    lastTime: string;
    count: number;
    unread: boolean;
  }[] {
    const sidebarQuery = (this.threadSearchTerm || '').trim().toLowerCase();
    const topQuery = (this.searchTerm || '').trim().toLowerCase();

    return (this.conversations || [])
      .filter((c) => {
        if (this.phoneFilter && c.phone_number !== this.phoneFilter) return false;
        const haystack = `${c.phone_number} ${c.display_name || ''} ${c.last_message || ''}`.toLowerCase();
        if (sidebarQuery && !haystack.includes(sidebarQuery)) return false;
        if (topQuery && !haystack.includes(topQuery)) return false;
        return true;
      })
      .map((c) => ({
        phone: c.phone_number,
        displayName: c.display_name || c.phone_number,
        lastMessage: c.last_message || '-',
        lastTime: c.last_message_at,
        count: c.message_count || 0,
        unread: !this.readPhoneThreads.has(c.phone_number),
      }));
  }

  /** Only verified numbers have readable history; the rest are informational. */
  getAvailablePhoneNumbers(): string[] {
    const set = new Set<string>();
    (this.account?.readable_numbers || []).forEach((p) => set.add(p));
    (this.conversations || []).forEach((c) => set.add(c.phone_number));
    return Array.from(set);
  }

  getActiveThreadMessages(): ClientLogItem[] {
    return this.threadMessages;
  }

  selectPhoneThread(phone: string): void {
    if (this.activeChatPhone === phone) return;
    this.activeChatPhone = phone;
    this.readPhoneThreads.add(phone);
    this.saveReadThreads();
    this.reloadActiveThread();
  }

  isThreadRead(phone: string): boolean {
    return this.readPhoneThreads.has(phone) || this.activeChatPhone === phone;
  }

  openPhoneChatThread(phone: string): void {
    if (!phone) return;
    this.reportType = 'chat';
    this.viewMode = 'chat';
    this.selectPhoneThread(phone);
  }

  closePhoneChatThread(): void {
    this.activeChatPhone = null;
    this.threadMessages = [];
  }

  getSelectedThreadCompanyId(): string | number {
    return this.account?.company_id ?? '-';
  }

  getActiveConversation(): ClientLogConversation | null {
    return (
      (this.conversations || []).find((c) => c.phone_number === this.activeChatPhone) || null
    );
  }

  getActiveThreadTitle(): string {
    const conversation = this.getActiveConversation();
    return conversation?.display_name || this.activeChatPhone || '';
  }

  /** A number that exists on the account but is not OTP-verified has no history. */
  isNumberVerified(phone: string): boolean {
    const number = (this.account?.numbers || []).find((n) => n.phone === phone);
    return number ? !!number.verified : true;
  }

  private loadReadThreads(): void {
    try {
      const saved = localStorage.getItem('whatsapp_read_threads');
      if (saved) {
        const list: string[] = JSON.parse(saved);
        if (Array.isArray(list)) this.readPhoneThreads = new Set(list);
      }
    } catch (e) {
      console.warn('Could not load read threads from localStorage:', e);
    }
  }

  private saveReadThreads(): void {
    try {
      localStorage.setItem(
        'whatsapp_read_threads',
        JSON.stringify(Array.from(this.readPhoneThreads))
      );
    } catch (e) {
      console.warn('Could not save read threads to localStorage:', e);
    }
  }

  // ── Detail drawer ──────────────────────────────────────────────────────────

  openDetailDrawer(item: any): void {
    this.selectedItem = item;
    this.rawEntries = this.buildRawEntries(item);
    this.drawerVisible = true;
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedItem = null;
    this.rawEntries = [];
  }

  private buildRawEntries(raw: any): { key: string; value: string }[] {
    if (!raw || typeof raw !== 'object') return [];
    return Object.keys(raw).map((key) => {
      const value = raw[key];
      if (value === null || value === undefined || value === '') {
        return { key, value: '-' };
      }
      if (typeof value === 'object') {
        return { key, value: JSON.stringify(value, null, 2) };
      }
      return { key, value: String(value) };
    });
  }

  // ── Field helpers ──────────────────────────────────────────────────────────

  getPhone(row: any): string {
    return row?.phone_number || row?.phone || '-';
  }

  getMessageText(row: any): string {
    return row?.body || '-';
  }

  getDirection(row: any): string {
    return row?.direction || 'inbound';
  }

  isInbound(row: any): boolean {
    return this.getDirection(row).toLowerCase().includes('in');
  }

  getDirectionClass(direction: string): string {
    const value = (direction || '').toLowerCase();
    if (value.includes('in')) return 'direction-in';
    if (value.includes('out')) return 'direction-out';
    return 'direction-default';
  }

  getSenderName(msg: any): string {
    const conversation = this.getActiveConversation();
    if (this.isInbound(msg)) {
      return conversation?.display_name || 'Luqta Participant';
    }
    return 'Luqta Assistant';
  }

  getSenderInitials(msg: any): string {
    const name = this.getSenderName(msg).replace(/^\+/, '').trim();
    if (!name) return 'U';
    const parts = name.split(/\s+/);
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    if (name.length >= 2) return name.substring(0, 2).toUpperCase();
    return name[0].toUpperCase();
  }

  formatDateTime(timestamp: any): string {
    if (!timestamp) return '-';
    const date = new Date(typeof timestamp === 'number' ? timestamp : String(timestamp));
    if (isNaN(date.getTime())) return String(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /** Date pickers give a local midnight; the API wants an ISO UTC boundary. */
  private toIso(date: Date, endOfDay: boolean): string {
    const d = new Date(date);
    if (endOfDay) d.setHours(23, 59, 59, 999);
    else d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key);
    return translated === key ? fallback : translated;
  }

  getPageRangeText(): string {
    const start = this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return `${start}-${end} of ${this.totalItems} items`;
  }

  // ── WhatsApp text formatting ───────────────────────────────────────────────
  //
  // The bot writes in WhatsApp's own markup: *bold*, _italic_, ~strikethrough~
  // and ```monospace```. Rendering the raw body leaves the markers visible, so
  // they are converted to real markup here.

  private static readonly WA_MARKERS: { marker: string; tag: string }[] = [
    { marker: '*', tag: 'strong' },
    { marker: '_', tag: 'em' },
    { marker: '~', tag: 's' },
  ];

  /**
   * Message body as HTML, with WhatsApp markup applied.
   *
   * The body is escaped before any markup is inserted, so message text can
   * never inject elements. The result is cached per message id because an
   * `[innerHTML]` binding would otherwise re-write the DOM on every change
   * detection pass.
   */
  formatMessageBody(row: any): SafeHtml {
    const body = this.getMessageText(row);
    const key = `${row?.id ?? ''}:${body.length}`;
    const cached = this.formattedBodies.get(key);
    if (cached) return cached;

    const html = this.sanitizer.bypassSecurityTrustHtml(this.toWhatsappHtml(body));
    this.formattedBodies.set(key, html);
    return html;
  }

  /** Same text with the markers stripped — for previews and tooltips. */
  getPlainMessageText(row: any): string {
    return this.stripWhatsappMarkup(this.getMessageText(row));
  }

  /** Strips the markers from an already-plain string (sidebar previews). */
  stripMarkup(text: string | null | undefined): string {
    return this.stripWhatsappMarkup(text || '');
  }

  private toWhatsappHtml(text: string): string {
    if (!text || text === '-') return text || '';

    let out = this.escapeHtml(text);

    // Monospace blocks are literal, so they are lifted out before the inline
    // markers run and put back afterwards.
    const blocks: string[] = [];
    out = out.replace(/```([\s\S]+?)```/g, (_m, code) => {
      blocks.push(`<code class="wa-mono">${code}</code>`);
      return `\u0000${blocks.length - 1}\u0000`;
    });

    for (const { marker, tag } of WhatsappConversationLogComponent.WA_MARKERS) {
      out = out.replace(this.markerPattern(marker), (_m, before, inner) => {
        return `${before}<${tag}>${inner}</${tag}>`;
      });
    }

    return out.replace(/\u0000(\d+)\u0000/g, (_m, i) => blocks[Number(i)] ?? '');
  }

  private stripWhatsappMarkup(text: string): string {
    if (!text) return '';
    let out = text.replace(/```([\s\S]+?)```/g, '$1');
    for (const { marker } of WhatsappConversationLogComponent.WA_MARKERS) {
      out = out.replace(this.markerPattern(marker), '$1$2');
    }
    return out;
  }

  /**
   * Matches one marker pair. Like WhatsApp, the opening marker must start a
   * word and the closing one must end it, so arithmetic and stray asterisks
   * are left alone.
   */
  private markerPattern(marker: string): RegExp {
    const m = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(
      `(^|[^\\w${m}])${m}([^\\s${m}][^${m}\n]*?[^\\s${m}]|[^\\s${m}])${m}(?![\\w${m}])`,
      'g'
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // ── Emoji picker ───────────────────────────────────────────────────────────

  getFilteredEmojis(): string[] {
    const list = this.emojiListMap[this.emojiCategory] || [];
    if (this.emojiSearchTerm.trim()) {
      const term = this.emojiSearchTerm.toLowerCase();
      return list.filter((e) => e.includes(term));
    }
    return list;
  }

  toggleEmojiPicker(): void {
    this.emojiPickerVisible = !this.emojiPickerVisible;
    if (this.emojiPickerVisible) this.attachmentMenuVisible = false;
  }

  addEmoji(emoji: string): void {
    this.newMessageText += emoji;
    this.emojiPickerVisible = false;
  }

  toggleAttachmentMenu(): void {
    this.attachmentMenuVisible = !this.attachmentMenuVisible;
    if (this.attachmentMenuVisible) this.emojiPickerVisible = false;
  }

  private loadEmojisFromApi(): void {
    const emojiApiUrl = 'https://raw.githubusercontent.com/github/gemoji/master/db/emoji.json';
    this.http.get<any[]>(emojiApiUrl).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        if (!Array.isArray(data) || data.length === 0) return;
        const smileys: string[] = [];
        const hands: string[] = [];
        const hearts: string[] = [];
        const objects: string[] = [];

        data.forEach((item) => {
          if (!item.emoji) return;
          const category = (item.category || '').toLowerCase();
          const desc = (item.description || '').toLowerCase();
          const tags = Array.isArray(item.tags) ? item.tags.join(' ') : '';
          const aliases = Array.isArray(item.aliases) ? item.aliases.join(' ') : '';
          const allText = `${desc} ${tags} ${aliases}`;

          if (category.includes('smile') || category.includes('emotion') || category.includes('people')) {
            if (allText.includes('hand') || allText.includes('finger') || allText.includes('thumb') || allText.includes('clap') || allText.includes('fist')) {
              hands.push(item.emoji);
            } else {
              smileys.push(item.emoji);
            }
          } else if (category.includes('symbol') || category.includes('flag') || allText.includes('heart') || allText.includes('love') || allText.includes('star')) {
            hearts.push(item.emoji);
          } else {
            objects.push(item.emoji);
          }
        });

        if (smileys.length) this.emojiListMap['smileys'] = smileys;
        if (hands.length) this.emojiListMap['hands'] = hands;
        if (hearts.length) this.emojiListMap['hearts'] = hearts;
        if (objects.length) this.emojiListMap['objects'] = objects;
      },
      error: (err) => {
        console.warn('Emoji API load fallback to default list:', err);
      },
    });
  }
}
